import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  NMForm,
  Row,
  Button,
  TextButton,
  NMPopup,
  NMWaitIndicator,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import {
  auditorActions,
  getReportFormatsList,
  getModelList,
  getModelDetails,
  getModelDetailsLoading,
} from "../../../../store";

const NewBlueprintFormFields = [
  {
    label: "Title",
    required: true,
    attribute: "title",
    typeRepresentation: "TEXT",
    placeholder: "Provide title",
    classes: { root: "w-100", label: "w-120-px" },
  },
  {
    label: "Description",
    required: true,
    attribute: "description",
    typeRepresentation: "TEXTAREA",
    rows: "15",
    placeholder: "Provide Description",
    classes: { root: "w-100", label: "w-120-px" },
  },
  {
    label: "Content",
    required: true,
    attribute: "content",
    typeRepresentation: "FILE",
    placeholder: "Attach Score Auditor v1 CSV file",
    accept: ".csv",
    classes: { root: "w-100", label: "w-110-px" },
    disableAttachAfterSelect: true,
  },
  {
    label: "Image",
    required: true,
    attribute: "image",
    typeRepresentation: "FILE",
    placeholder: "Attach Audit Image PNG file",
    accept: "image/png, image/jpeg",
    classes: { root: "w-100", label: "w-110-px" },
    disableAttachAfterSelect: true,
  },
  {
    label: "Results",
    required: true,
    attribute: "results",
    typeRepresentation: "FILE",
    accept: ".csv",
    placeholder: "Attach Audit Results CSV file",
    classes: { root: "w-100", label: "w-110-px" },
    disableAttachAfterSelect: true,
  },
  {
    label: "Report Format",
    required: true,
    attribute: "report_format",
    typeRepresentation: "DROPDOWN",
    placeholder: "ISO 27001",
    classes: { root: "w-100", label: "w-120-px" },
  },
];

const firstFile = (v) => (Array.isArray(v) && v.length ? v[0] : null);

export const CreateNewAuditBlueprintScreen = (props) => {
  const { create, edit } = props;
  const mode = create ? "create" : edit ? "edit" : "view";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { blueprintId } = useParams(); // guid
  const reportFormats = useSelector(getReportFormatsList);
  const modelList = useSelector(getModelList);
  const modelDetails = useSelector(getModelDetails);
  const modelDetailsLoading = useSelector(getModelDetailsLoading);
  const _formRef = React.useRef(null);
  const [state, setState] = React.useState({
    formData: {},
    canSubmit: false,
    isSubmitting: false,
    showLoadingPopup: false,
  });

  const pageTitle = React.useMemo(() => {
    if (mode === "create") return "Create New Blueprint";
    if (mode === "edit") return "Update Blueprint";
    return "Blueprint Details";
  }, [mode]);

  const primaryLabel = React.useMemo(() => {
    if (mode === "create") return "Create";
    if (mode === "edit") return "Update";
    return "Edit";
  }, [mode]);

  const model = React.useMemo(() => {
    if (!blueprintId || !modelList.length) return null;
    return modelList.find((x) => x.guid === blueprintId) || null;
  }, [blueprintId, modelList]);

  const modelId = model?.id || null;

  React.useEffect(() => {
    dispatch(auditorActions.fetchReportFormats());
  }, [dispatch]);

  // ----- fetch list/details for edit/view -----
  React.useEffect(() => {
    if (!(mode === "edit" || mode === "view")) return;
    if (!blueprintId) return;

    // If landed directly, ensure list exists
    if (!modelList.length) {
      dispatch(auditorActions.fetchModelList());
      return;
    }

    if (!modelId) return;

    dispatch(auditorActions.fetchModelDetails({ id: modelId }));
  }, [dispatch, mode, blueprintId, modelList.length, modelId]);

  // when details arrive, prefill
  React.useEffect(() => {
    if (!(mode === "edit" || mode === "view")) return;
    if (!modelDetails) return;

    const createDummyFile = (fileName) => {
      if (!fileName) return [];
      return [new File([""], fileName, { type: "application/octet-stream" })];
    };

    const next = {
      title: modelDetails.title || "",
      description: modelDetails.desc || "",
      report_format: modelDetails.report_format || "",
      image: createDummyFile(modelDetails.icon_file_name),
      results: createDummyFile(modelDetails.cntrl_result_file_name),
      content: createDummyFile(modelDetails.model_file_name),
    };
    updateState({ formData: next });
    _formRef.current?.updateFormData?.(next);

    if (mode === "edit") {
      const ok = _formRef.current?.validate?.(false) === true;
      updateState({ canSubmit: ok });
    }
  }, [mode, modelDetails]);

  const updateState = (iNewState) => {
    setState((prev) => ({ ...prev, ...iNewState }));
  };

  const getOptions = React.useCallback(
    (attribute) => {
      if (attribute === "report_format") {
        return reportFormats.map((rf) => ({ id: String(rf), label: rf }));
      }
      return [];
    },
    [reportFormats],
  );

  const handleCancel = () => {
    if (mode === "edit") return navigate(`/A/${blueprintId}`);
    return navigate(`/A`);
  };

  const handleChange = () => {
    const ok = _formRef.current?.validate?.(false) === true;
    updateState({ canSubmit: ok });
  };

  const buildCreatePayload = React.useCallback(() => {
    const values = _formRef.current?.getValues?.() || {};
    const image = firstFile(values.image);
    const content = firstFile(values.content);
    const results = firstFile(values.results);

    const formdata = [
      { key: "title", value: values.title || "", type: "text" },
      { key: "desc", value: values.description || "", type: "text" },
      {
        key: "report_format",
        value: values.report_format || "",
        type: "text",
      },
      ...(image ? [{ key: "icon", value: image, type: "file" }] : []),
      ...(content ? [{ key: "file", value: content, type: "file" }] : []),
      ...(results
        ? [{ key: "cntrl_result_options", value: results, type: "file" }]
        : []),
    ];

    return { mode: "formdata", formdata };
  }, []);

  const buildUpdatePayload = React.useCallback(() => {
    const dirtyValues =
      _formRef.current?.getValues?.({ dirtyOnly: true }) || {};

    const formdata = [
      {
        key: "model_id",
        value: blueprintId,
        type: "text",
      },
    ];

    // TEXT FIELDS
    if (dirtyValues.title !== undefined) {
      formdata.push({
        key: "title",
        value: dirtyValues.title,
        type: "text",
      });
    }

    if (dirtyValues.description !== undefined) {
      formdata.push({
        key: "desc",
        value: dirtyValues.description,
        type: "text",
      });
    }

    if (dirtyValues.report_format !== undefined) {
      formdata.push({
        key: "report_format",
        value: dirtyValues.report_format,
        type: "text",
      });
    }

    // FILE FIELDS
    if (dirtyValues.image) {
      const image = firstFile(dirtyValues.image);
      if (image && image.size > 0) {
        formdata.push({
          key: "icon",
          value: image,
          type: "file",
        });
      }
    }

    if (dirtyValues.content) {
      const file = firstFile(dirtyValues.content);
      if (file && file.size > 0) {
        formdata.push({
          key: "file",
          value: file,
          type: "file",
        });
      }
    }

    if (dirtyValues.results) {
      const resultFile = firstFile(dirtyValues.results);
      if (resultFile && resultFile.size > 0) {
        formdata.push({
          key: "cntrl_result_options",
          value: resultFile,
          type: "file",
        });
      }
    }

    return { mode: "formdata", formdata };
  }, [blueprintId]);

  const handleConfirm = () => {
    if (mode === "view") return navigate("edit");

    let action;
    if (mode === "create") {
      const payload = buildCreatePayload();
      action = auditorActions.createModel(payload);
    }

    if (mode === "edit") {
      const payload = buildUpdatePayload();

      // Prevent empty update
      if (payload.formdata.length === 1) {
        return; // only model_id exists, nothing changed
      }
      action = auditorActions.updateModel(payload);
    }
    if (action) {
      updateState({ showLoadingPopup: true, isSubmitting: true });
      action.onFailure = () => {
        updateState({ isSubmitting: false, showLoadingPopup: false });
      };
      action.onComplete = () => {
        updateState({ isSubmitting: false, showLoadingPopup: false });
        navigate("/A");
      };
      dispatch(action);
    }
  };

  return (
    <div className="o-auto h-100 pl-20 pt-50">
      <Row
        className="text-action fs-14"
        onClick={() => navigate(-1)}
        style={{ cursor: "pointer" }}
      >
        Back
      </Row>
      <div className="c238787 mt-10 exo2 f6 bolder">{pageTitle}</div>
      <Row className="pl-10 mt-30 w-50">
        <NMForm
          ref={_formRef}
          width={850}
          variant="INFO"
          fields={NewBlueprintFormFields}
          onChange={handleChange}
          formData={state.formData}
          getOptions={getOptions}
        />
      </Row>
      <Row className="pl-10 mt-10 mb-20 w-50 gap-10" justify="end">
        <TextButton
          icon="CANCEL"
          label="Cancel"
          disabled={state.isSubmitting}
          variant="ACTION"
          onClick={handleCancel}
        />
        <Button
          loading={state.isSubmitting}
          icon={mode === "view" ? "EDIT" : "SQUARE_NEXT"}
          label={primaryLabel}
          onClick={handleConfirm}
          disabled={
            mode !== "view" && (!state.canSubmit || modelDetailsLoading)
          }
        />
      </Row>
      {state.showLoadingPopup && (
        <NMPopup visible classes={{ root: "audit-wait-popup" }}>
          <NMWaitIndicator
            hideSpinner={true}
            classes={{ root: "mb-20", message: "mt-10" }}
            message={`Hold on while the Audit is being ${create ? "created" : "updated"}...`}
          />
        </NMPopup>
      )}
    </div>
  );
};
